import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useId } from "react";
export function Tabs({ tabs, activeTab: controlledActiveTab, defaultActiveTab, onTabChange, variant = "underline", size = "md", fullWidth = false, className = "", tabListClassName = "", tabPanelsClassName = "", renderTab, }) {
    const baseId = useId();
    // Determine if controlled or uncontrolled
    const isControlled = controlledActiveTab !== undefined;
    const [internalActiveTab, setInternalActiveTab] = useState(defaultActiveTab || tabs.find((t) => !t.disabled)?.id || tabs[0]?.id);
    const activeTab = isControlled ? controlledActiveTab : internalActiveTab;
    const handleTabClick = (tabId, disabled) => {
        if (disabled)
            return;
        if (!isControlled) {
            setInternalActiveTab(tabId);
        }
        onTabChange?.(tabId);
    };
    const sizeClasses = {
        sm: "text-sm px-3 py-1.5",
        md: "text-base px-4 py-2",
        lg: "text-lg px-5 py-3",
    };
    const getTabClasses = (tab, isActive) => {
        const baseClasses = `
      touch-target inline-flex items-center justify-center font-medium
      transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-accent
      ${sizeClasses[size]}
      ${fullWidth ? "flex-1" : ""}
      ${tab.disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
    `;
        if (variant === "underline") {
            return `${baseClasses} border-b-2 ${isActive
                ? "border-[color:var(--color-primary,var(--color-accent))] text-[color:var(--color-primary,var(--color-accent))]"
                : "border-transparent text-textSecondary hover:text-textPrimary"}`;
        }
        if (variant === "pills") {
            return `${baseClasses} rounded-full ${isActive
                ? "bg-textPrimary text-background"
                : "bg-transparent text-textSecondary hover:bg-accent hover:text-textPrimary"}`;
        }
        // enclosed variant
        return `${baseClasses} border border-border rounded-t-lg ${isActive
            ? "bg-surface text-textPrimary border-b-surface -mb-px"
            : "bg-transparent text-textSecondary hover:bg-accent hover:text-textPrimary"}`;
    };
    const tabListClasses = `
    flex ${fullWidth ? "w-full" : ""}
    ${variant === "underline" ? "border-b border-border space-x-1 overflow-x-auto" : ""}
    ${variant === "pills" ? "space-x-2 overflow-x-auto" : ""}
    ${variant === "enclosed" ? "border-b border-border space-x-1 overflow-x-auto" : ""}
    ${tabListClassName}
  `;
    return (_jsxs("div", { className: `w-full ${className}`, children: [_jsx("div", { role: "tablist", className: tabListClasses, "aria-label": "Tabs", children: tabs.map((tab) => {
                    const isActive = tab.id === activeTab;
                    const tabId = `${baseId}-tab-${tab.id}`;
                    const panelId = `${baseId}-panel-${tab.id}`;
                    return (_jsx("button", { id: tabId, role: "tab", "aria-selected": isActive, "aria-controls": panelId, "aria-disabled": tab.disabled, tabIndex: isActive ? 0 : -1, onClick: () => handleTabClick(tab.id, tab.disabled), className: getTabClasses(tab, isActive), disabled: tab.disabled, children: renderTab ? (renderTab(tab, isActive)) : (_jsxs(_Fragment, { children: [tab.icon && _jsx("span", { className: "mr-2 flex-shrink-0", children: tab.icon }), _jsx("span", { className: "whitespace-nowrap", children: tab.label }), tab.badge && (_jsx("span", { className: `ml-2 flex-shrink-0 rounded-full px-2 py-0.5 text-xs ${isActive && variant === "pills"
                                        ? "bg-background text-textPrimary"
                                        : "bg-accent text-textPrimary"}`, children: tab.badge }))] })) }, tab.id));
                }) }), tabs.some((tab) => tab.content) && (_jsx("div", { className: `mt-4 ${tabPanelsClassName}`, children: tabs.map((tab) => {
                    const isActive = tab.id === activeTab;
                    const tabId = `${baseId}-tab-${tab.id}`;
                    const panelId = `${baseId}-panel-${tab.id}`;
                    if (!tab.content)
                        return null;
                    return (_jsx("div", { id: panelId, role: "tabpanel", "aria-labelledby": tabId, hidden: !isActive, tabIndex: 0, className: isActive ? "" : "hidden", children: tab.content }, tab.id));
                }) }))] }));
}
export function TabList({ children, className = "", variant = "underline", fullWidth = false }) {
    const variantClasses = {
        underline: "border-b border-border space-x-1",
        pills: "space-x-2",
        enclosed: "border-b border-border space-x-1",
    };
    return (_jsx("div", { role: "tablist", className: `flex overflow-x-auto ${fullWidth ? "w-full" : ""} ${variantClasses[variant]} ${className}`, children: children }));
}
export function TabTrigger({ children, disabled, className = "" }) {
    // This would need context to work properly - simplified version
    const baseClasses = `
    touch-target inline-flex items-center justify-center px-4 py-2 text-base font-medium
    transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-accent
    ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
    ${className}
  `;
    return (_jsx("button", { role: "tab", disabled: disabled, className: baseClasses, children: children }));
}
export function TabPanels({ children, className = "" }) {
    return _jsx("div", { className: `mt-4 ${className}`, children: children });
}
export function TabPanel({ children, className = "" }) {
    // This would need context to work properly - simplified version
    return (_jsx("div", { role: "tabpanel", tabIndex: 0, className: className, children: children }));
}
